import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { setTimeFormat } from "@/lib/time";
import { colorOf } from "@/lib/constants";
import { memberAccentHex } from "@/lib/memberColors";
import { useI18n } from "@/lib/i18n";
import { moodEmoji, computeVibe, petWalkVibe } from "@/lib/vibe";
import { mergeWithBirthdays, selectStripEvents, dayLabel, kindOf, fmtEventWhen, eventEmoji, eventCountdown } from "@/lib/events";
import { isTeenPlusFamily } from "@/lib/birthdays";
import BirthdayBanner from "@/components/BirthdayBanner";
import ConfettiBurst from "@/components/ConfettiBurst";
import { X } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { isDeviceCalendarOnly, getDeviceDashboardMode } from "@/lib/deviceCalendarOnly";
// Greeting pools + picker live in /lib/greeting.js so DisplayMode and
// the in-app Dashboard share the exact same vocabulary and band cuts.
// Adjust copy there once and both surfaces update.
import { greetingFor } from "@/lib/greeting";

// Ambient display mode. Full-screen editorial placard for a shared iPad on
// the kitchen counter. Shows:
//   • A giant time-aware greeting
//   • Family members as huge tinted cards — faded if they haven't checked
//     in today, full colour if they have
//   • The next few upcoming events
//   • Big clock in the header, auto-refresh every 60s, no nav chrome.
// Also requests a screen wake lock so the iPad never sleeps while it's
// showing this page.

const tick = (cb, ms) => {
  cb();
  const id = setInterval(cb, ms);
  return () => clearInterval(id);
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Rotating greeting pools. Picked by a deterministic seed (date + band) so
// the greeting stays stable within each band (no flicker) but varies across
// bands and across days.
// Greeting pools + picker live in /lib/greeting.js so DisplayMode and
// the in-app Dashboard share the exact same vocabulary and band cuts.
// Adjust copy there once and both surfaces update.

const fmtTime = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const fmtDate = (d, lang = "en") =>
  d.toLocaleDateString(lang === "da" ? "da-DK" : "en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

// Local-day "YYYY-MM-DD" key for an ISO timestamp. Same convention
// used by dayLabel() so the two stay in lockstep.
const dayKeyOf = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Calendar tint softener — when the family has older kids, pastel
// category tints can feel "kids-app", so we fade them toward the
// neutral background. Matches the CalendarSheet 60/40 blend so a
// "Hjem til Malene" card looks identical between the calendar list
// and Display Mode (used to be 80/20 here, which washed it out and
// looked inconsistent on shared iPads showing both views).
const softenTint = (tint, fade) =>
  fade ? `color-mix(in srgb, var(--bg) 50%, ${tint})` : tint;

// How long an event "lasts" when no end time is set. Used by the
// hero buckets so a card that pops at the top stays there for the
// full reasonable window — e.g. a fishing trip that started at 13:00
// keeps shouting "Happening now" until its end (or the kind default
// below) instead of vanishing 60 minutes in.
const KIND_DEFAULT_DURATION_MS = {
  activity: 60 * 60 * 1000,        // sport / activity
  dinner: 60 * 60 * 1000,
  event: 3 * 60 * 60 * 1000,       // event / party
  day_trip: 7 * 60 * 60 * 1000,    // capped to end-of-day below
  school_event: 60 * 60 * 1000,
  travel: 60 * 60 * 1000,          // capped to end-of-day if all-day
  appointment: 60 * 60 * 1000,
  other: 60 * 60 * 1000,
};
const isAllDayLocal = (iso) => {
  const d = new Date(iso);
  return d.getHours() === 0 && d.getMinutes() === 0;
};
const endOfLocalDayMs = (ms) => {
  const d = new Date(ms);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};
// Effective end timestamp for a given event. Honours an explicit
// `ends_at` first; otherwise falls back to the kind's default
// duration. All-day events (00:00 start, no end) coast until the end
// of the local day so a logged "day trip" shows as live the whole
// day. Family birthdays (synthesized) are end-of-day too. Friend
// birthdays set as all-day are bucketed separately (see hero logic).
const effectiveEndMs = (ev) => {
  if (!ev?.starts_at) return null;
  const startMs = new Date(ev.starts_at).getTime();
  if (Number.isNaN(startMs)) return null;
  if (ev.ends_at) {
    const e = new Date(ev.ends_at).getTime();
    if (!Number.isNaN(e)) return e;
  }
  if (isAllDayLocal(ev.starts_at)) return endOfLocalDayMs(startMs);
  const dur = KIND_DEFAULT_DURATION_MS[ev.kind] ?? 60 * 60 * 1000;
  // Cap to end-of-day so a 19:00 day_trip doesn't bleed into tomorrow.
  return Math.min(startMs + dur, endOfLocalDayMs(startMs));
};

// Returns true when `ev` overlaps the given local date key — i.e. it
// starts on that day, ends on that day, or spans across it. Without
// this a Fri→Sun Paris trip only showed on Friday because
// `dayKeyOf(starts_at) === dayKey` is false on Sat/Sun even though
// the family is very much in Paris. Strict string compare on
// YYYY-MM-DD works because lexicographic order matches calendar order.
const eventIsOnDay = (ev, dayKey) => {
  if (!ev?.starts_at) return false;
  const startKey = dayKeyOf(ev.starts_at);
  if (startKey === dayKey) return true;
  if (!ev.ends_at) return false;
  const endKey = dayKeyOf(ev.ends_at);
  if (endKey === dayKey) return true;
  return startKey < dayKey && dayKey < endKey;
};

// Subline shown under the greeting in calendar-only mode. We keep it tight
// — today's count, plus tomorrow's count when there's anything on. No big
// "N more coming up" tail; the calendar groups below already show that.
// Locale-aware — pass the active i18n lang so the copy stays consistent
// with the rest of Display Mode when a Danish family opens it.
const tomorrowKey = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const pluralThing = (n, lang = "en") => {
  if (lang === "da") return `${n} ${n === 1 ? "ting" : "ting"}`; // Danish "ting" is invariant
  return `${n} thing${n === 1 ? "" : "s"}`;
};
const calendarSubline = (events, lang = "en") => {
  const today = todayKey();
  const tomorrow = tomorrowKey();
  const list = events || [];
  const nowMs = Date.now();
  // "Things today" = current + future events that overlap today.
  // `eventIsOnDay` matches events that start today, end today, OR
  // span across today (multi-day trips), so a Fri→Sun Paris trip
  // is counted on Fri/Sat/Sun. Events whose effective end is
  // already in the past are excluded so the count stays consistent
  // with what the display actually renders below.
  // Multi-day away trips are not "things" you do today — they're an
  // ambient status (rendered as the away strip at the top). Excluded
  // from the today/tomorrow counts so "2 things today" doesn't
  // double-count Momme's week in Tyrkiet.
  const isMultiDayAway = (e) => {
    if (e.kind !== "travel" && e.kind !== "day_trip") return false;
    if (!e.ends_at) return false;
    const sKey = dayKeyOf(e.starts_at);
    const eKey = dayKeyOf(e.ends_at);
    return Boolean(sKey && eKey && sKey !== eKey);
  };
  const isStillTodayish = (e) => {
    if (isMultiDayAway(e)) return false;
    if (!eventIsOnDay(e, today)) return false;
    const endMs = effectiveEndMs(e);
    return endMs === null || endMs >= nowMs;
  };
  const todayCount = list.filter(isStillTodayish).length;
  const tomorrowCount = list.filter(
    (e) => !isMultiDayAway(e) && eventIsOnDay(e, tomorrow),
  ).length;
  if (todayCount === 0 && tomorrowCount === 0) {
    return lang === "da"
      ? "Ingenting på programmet. Nyd det."
      : "Nothing on. Pour a slow coffee.";
  }
  const parts = [];
  if (todayCount > 0) parts.push(lang === "da"
    ? `${pluralThing(todayCount, "da")} i dag`
    : `${pluralThing(todayCount)} today`);
  if (tomorrowCount > 0) parts.push(lang === "da"
    ? `${pluralThing(tomorrowCount, "da")} i morgen`
    : `${pluralThing(tomorrowCount)} tomorrow`);
  return parts.join(" • ");
};

export default function DisplayMode() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [now, setNow] = useState(new Date());
  const [members, setMembers] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  // Confetti replay counter — bumped on every famfeel:celebrate
  // CustomEvent so the kitchen iPad becomes the party projector when
  // someone walks past and taps the birthday banner.
  const [celebrateKey, setCelebrateKey] = useState(0);
  // Lightbox for the event attachment on the Today hero card. Tap
  // the thumbnail → photo expands fullscreen over a black backdrop.
  // Same affordance as EventInfoPage so families only learn the
  // pattern once.
  const [lightboxSrc, setLightboxSrc] = useState(null);
  useEffect(() => {
    const onCelebrate = () => setCelebrateKey((k) => k + 1);
    window.addEventListener("famfeel:celebrate", onCelebrate);
    return () => window.removeEventListener("famfeel:celebrate", onCelebrate);
  }, []);

  useSeo({
    title: "FamFeel — Ambient display",
    description: "Shared family dashboard for a kitchen iPad.",
    path: "/display",
  });

  // Redirect to /app if not signed in.
  useEffect(() => {
    if (!auth.getToken()) {
      navigate("/app", { replace: true });
    }
  }, [navigate]);

  // Clock ticker — update every 15s so seconds don't thrash.
  useEffect(() => tick(() => setNow(new Date()), 15000), []);

  // Data fetcher — refresh every 60s.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [ms, cs, evs, s] = await Promise.all([
          api.listMembers(),
          api.todayCheckins(),
          api.upcomingEvents().catch(() => []),
          api.getFamilySettings().catch(() => null),
        ]);
        if (cancelled) return;
        setMembers(ms || []);
        setCheckins(cs || []);
        const merged = mergeWithBirthdays(evs || [], ms || []);
        setEvents(selectStripEvents(merged));
        // Calendar-only display mode uses a bigger pool so the grid stays
        // visually dense on a kitchen iPad — cap at 24 day cells.
        setAllEvents(
          selectStripEvents(merged, { targetMin: 24, hardMax: 48 })
        );
        setSettings(s);
        // Display Mode is often the longest-running session — kitchen
        // iPad stays open for hours. Stamp the time format here too so
        // the format updates reflect on the kitchen iPad next time the
        // settings poll re-runs.
        if (s) setTimeFormat(s.time_format);
        setLoaded(true);
      } catch {/* ignore transient errors */}
    };
    return tick(load, 60000);
  }, []);

  // Live-sync: when any device toggles "color-code events by member"
  // the event-bridge fires this CustomEvent. Mirror the change into
  // the local settings state so the kitchen iPad swaps to neutral
  // cards + member stripes instantly (instead of waiting up to 60s
  // for the next data poll).
  useEffect(() => {
    const onSettingsUpdated = (e) => {
      if (e?.detail?.settings) {
        setSettings(e.detail.settings);
      } else if (typeof e?.detail?.color_code_by_member === "boolean") {
        setSettings((prev) =>
          prev ? { ...prev, color_code_by_member: e.detail.color_code_by_member } : prev,
        );
      }
    };
    window.addEventListener("famfeel:family-settings-updated", onSettingsUpdated);
    return () => window.removeEventListener("famfeel:family-settings-updated", onSettingsUpdated);
  }, []);

  // Screen wake lock — iPad / phone stays lit while this page is visible.
  useEffect(() => {
    let lock = null;
    let released = false;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          lock = await navigator.wakeLock.request("screen");
          lock.addEventListener?.("release", () => {});
        }
      } catch {/* user denied or unsupported */}
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && !released) acquire();
    };
    acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      try { lock && lock.release && lock.release(); } catch {/* noop */}
    };
  }, []);

  // Latest check-in per member
  const checkinByMember = useMemo(() => {
    const tk = todayKey();
    const map = {};
    for (const c of checkins) {
      if (!c?.member_id) continue;
      if ((c.date || "").slice(0, 10) !== tk) continue;
      const prev = map[c.member_id];
      if (!prev || (c.created_at || "") > (prev.created_at || "")) {
        map[c.member_id] = c;
      }
    }
    return map;
  }, [checkins]);

  // Dashboard mode: family-wide > per-device > "full".
  const deviceMode = getDeviceDashboardMode();
  const familyMode =
    settings?.dashboard_mode ||
    (settings?.calendar_only ? "calendar" : "full");
  const dashboardMode = deviceMode || familyMode;
  const calendarOnly = dashboardMode === "calendar";
  const petsOnly = dashboardMode === "pets";

  const visibleMembers = useMemo(() => {
    if (petsOnly) return members.filter((m) => m.role === "pet");
    // Houses mode: when the device is currently "inside" a house
    // (Dashboard persists this to localStorage), Display Mode should
    // only project that house's members + any shared kids/pets. This
    // keeps kitchen iPads in Dad's house from showing Mum's tile and
    // vice versa. When the device hasn't entered a house yet (front
    // screen), fall back to all members since the whole family
    // dashboard is in play.
    let activeHouseId = null;
    try {
      activeHouseId = localStorage.getItem("ff_active_house") || null;
    } catch {/* private mode, treat as no active house */}
    const houseScoped = (list) => {
      if (!activeHouseId) return list;
      return list.filter((m) => {
        if (m.house_id === activeHouseId) return true;
        if (m.role !== "kid" && m.role !== "pet") return false;
        const p = m.cross_house_privacy || {};
        return p.mood !== false || p.status !== false;
      });
    };
    const humans = houseScoped(members.filter((m) => m.role !== "pet"));
    const pets = houseScoped(members.filter((m) => m.role === "pet"));
    return humans.concat(pets);
  }, [members, petsOnly]);

  // Rotate a soft highlight through the member tiles every ~15 seconds — adds
  // a quiet sense of life so the display doesn't feel frozen.
  useEffect(() => {
    if (visibleMembers.length === 0) return;
    let idx = 0;
    setHighlightIdx(0);
    const id = setInterval(() => {
      idx = (idx + 1) % visibleMembers.length;
      setHighlightIdx(idx);
    }, 15000);
    return () => clearInterval(id);
  }, [visibleMembers.length]);

  // Late-night mode: after 22:00 (and before 06:00) dim the whole display
  // so it doesn't light the kitchen — bedroom etiquette.
  const hour = now.getHours();
  const isLateNight = hour >= 22 || hour < 6;

  // Teen-plus household → whisper calendar kind tints toward paper so
  // the Display doesn't feel childish. Matches the dashboard's
  // `compactLayout` behaviour for parity between screens.
  const fadeCal = true;

  const loggedCount = visibleMembers.filter(
    (m) => {
      const c = checkinByMember[m.id];
      if (!c) return false;
      if (c.mood != null || c.sleep != null) return true;
      if (m.role === "pet") {
        // Pets: only count a pet as "checked in" when a REAL care action
        // happened today — not merely a scheduled future walk. Mirrors
        // Dashboard.loggedCount and vibe.js petTouched.
        return (
          (c.walks && c.walks.length > 0) ||
          !!c.fed_at ||
          !!c.water_change_at ||
          (c.feeds && c.feeds.length > 0) ||
          (c.water_changes && c.water_changes.length > 0)
        );
      }
      return false;
    }
  ).length;

  const greeting = greetingFor(now, { calendarOnly, lang });

  if (!loaded) {
    return (
      <div className="fixed inset-0 bg-[color:var(--bg)] flex items-center justify-center">
        <div className="font-display-italic text-5xl text-[color:var(--ink-3)]">
          FamFeel
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-[color:var(--bg)] text-[color:var(--ink)] overflow-y-auto md:overflow-hidden flex flex-col"
      data-testid="display-mode"
    >
      {/* Ambient warmth — a very slow (18s) warm-tone drift behind
          everything. Reads as "the paper is alive" from the kitchen
          without ever demanding attention. Hidden during late-night
          mode where the amber lamp does the work. */}
      {!isLateNight && <div className="ff-ambient-warmth" aria-hidden="true" />}
      {/* Late-night amber overlay — warms and dims the whole display without
          washing out contrast. Uses multiply blend so dark text stays dark and
          bright backgrounds turn a calm amber, like a bedside lamp. */}
      {isLateNight && (
        <div
          className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-700"
          style={{
            background: "rgba(26, 17, 6, 0.72)",
            mixBlendMode: "multiply",
          }}
          aria-hidden="true"
        />
      )}
      {/* Exit button (top-right) */}
      <button
        onClick={() => navigate("/app")}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[color:var(--bg-soft)] hover:bg-[color:var(--surface)] flex items-center justify-center z-10"
        aria-label="Exit display mode"
        data-testid="display-exit"
      >
        <X className="w-4 h-4" strokeWidth={1.75} />
      </button>

      {/* Header — clock + greeting */}
      <header className="px-6 sm:px-8 md:px-16 pt-6 sm:pt-10 md:pt-14">
        <div className="label-tiny text-[color:var(--ink-3)]">
          {fmtDate(now, lang)}
        </div>
        <div className="flex items-baseline justify-between gap-6 flex-wrap mt-1">
          <h1
            className="font-display-italic text-4xl sm:text-5xl md:text-7xl leading-[0.95] tracking-tight"
            data-testid="display-greeting"
          >
            {greeting}
          </h1>
          <div className="font-display text-3xl sm:text-4xl md:text-6xl tabular tracking-tight leading-none text-[color:var(--ink)]">
            {fmtTime(now)}
          </div>
        </div>
        <div className="label-tiny text-[color:var(--ink-2)] mt-3">
          {calendarOnly
            ? calendarSubline(allEvents, lang)
            : (lang === "da"
                ? `${loggedCount} af ${visibleMembers.length} tjekket ind i dag`
                : `${loggedCount} of ${visibleMembers.length} checked in today`)}
        </div>
        {/* BirthdayBanner removed — the headline + event card below
            already surface the birthday. Confetti burst still fires
            via the event row's tap. */}
      </header>

      {/* Main content — member grid in normal mode, calendar grid in calendar-only */}
      {calendarOnly ? (
        <main
          className="flex-1 px-8 md:px-16 py-6 md:py-10 overflow-hidden"
          data-testid="display-calendar-grid"
        >
          {allEvents.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="font-display-italic text-4xl text-[color:var(--ink-3)] text-center">
                {lang === "da" ? "Ingenting på kalenderen endnu." : "Nothing on the calendar yet."}
              </div>
            </div>
          ) : (
            <CalendarGroups
              events={allEvents.slice(0, 60)}
              members={members}
              navigate={navigate}
              fadeCal={fadeCal}
              colorCodeByMember={!!settings?.color_code_by_member}
              onOpenLightbox={setLightboxSrc}
            />
          )}
        </main>
      ) : (
        <main className="flex-1 px-6 sm:px-8 md:px-16 py-4 sm:py-8 md:py-12 flex items-center">
          <MembersGrid
            members={visibleMembers}
            checkinByMember={checkinByMember}
            highlightIdx={highlightIdx}
            onTap={(m) => {
              // Handing off to the dashboard: Dashboard.js reads this on mount
              // and auto-opens the check-in dialog for this member. Pets are
              // fine too — openCheckin() handles the pet-walk flow.
              try {
                sessionStorage.setItem("ff_pending_checkin", m.id);
              } catch {}
              navigate("/app");
            }}
          />
        </main>
      )}

      {/* Upcoming events strip (hidden in calendar-only mode — the whole
          screen is already the calendar) */}
      {!calendarOnly && events.length > 0 && (
        <footer className="px-6 sm:px-8 md:px-16 pb-6 sm:pb-8 md:pb-10">
          <div className="label-tiny text-[color:var(--ink-3)] mb-3">Coming up</div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar ff-stagger">
            {events.slice(0, 5).map((ev, i) => {
              const d = ev.starts_at ? new Date(ev.starts_at) : null;
              const key = d
                ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
                : null;
              // Color-code-by-member mode also applies to the bottom
              // strip on non-calendar Display modes. Pulls assigned
              // members and renders a thin right-edge accent rail
              // so the kitchen iPad reads as one consistent system.
              const stripAttendees = (ev.member_ids || [])
                .map((id) => members.find((m) => m.id === id))
                .filter((m) => m && (ev.source === "birthday" ? true : m.role !== "pet"));
              const stripShowStripe =
                !!settings?.color_code_by_member && stripAttendees.length > 0;
              return (
                <button
                  key={ev.id || `${ev.title}-${i}`}
                  type="button"
                  onClick={() => navigate(`/event/${encodeURIComponent(ev.id)}`, { state: { event: ev, from: "/display" } })}
                  className="relative overflow-hidden flex-shrink-0 rounded-xl border border-[color:var(--line)] px-5 py-3 bg-[color:var(--bg-soft)] text-left hover:bg-[color:var(--surface)] transition-colors"
                  data-testid={`display-upcoming-${ev.id}`}
                  style={
                    stripShowStripe
                      ? { paddingRight: "22px" }
                      : undefined
                  }
                >
                  {stripShowStripe && (
                    <div
                      className="absolute top-0 right-0 bottom-0 flex flex-col rounded-r-xl overflow-hidden pointer-events-none"
                      style={{ width: "16px" }}
                      aria-hidden="true"
                      data-testid={`display-upcoming-accents-${ev.id}`}
                    >
                      {stripAttendees.slice(0, 6).map((m) => (
                        <span
                          key={m.id}
                          className="flex-1 block"
                          style={{ background: memberAccentHex(m) }}
                          title={m.name}
                        />
                      ))}
                    </div>
                  )}
                  <div className="label-tiny text-[color:var(--ink)]">
                    {key ? dayLabel(key, lang) : ""}
                  </div>
                  <div className="font-display text-xl leading-tight tracking-tight mt-0.5 whitespace-nowrap text-[color:var(--accent)]">
                    {ev.title}
                    {ev.attachment_image && (
                      <span
                        className="ml-1.5 text-[14px] opacity-70 align-middle"
                        title="Photo attached"
                        data-testid={`display-upcoming-attachment-${ev.id}`}
                      >
                        📎
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </footer>
      )}
      {/* Confetti replay — bumps celebrateKey on every
          famfeel:celebrate event so the kitchen iPad bursts fresh
          each time someone taps the birthday banner. */}
      {celebrateKey > 0 && <ConfettiBurst key={celebrateKey} />}
      {/* Fullscreen lightbox — tap the Today hero thumbnail to open.
          Same black-backdrop pattern as EventInfoPage so the
          affordance is consistent across the app. */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setLightboxSrc(null)}
          data-testid="display-attachment-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white inline-flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="display-attachment-lightbox-close"
          >
            <X className="w-5 h-5" strokeWidth={2} />
          </button>
          <img
            src={lightboxSrc}
            alt="Event attachment"
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function MembersGrid({ members, checkinByMember, highlightIdx, onTap }) {
  const count = members.length;
  // Responsive grid: 2 cols for up to 4, 3 for up to 9, 4 for more.
  const cols = count <= 2 ? 2 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
  return (
    <div
      className="w-full grid gap-5 md:gap-7"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {members.map((m, i) => (
        <MemberTile
          key={m.id}
          m={m}
          c={checkinByMember[m.id]}
          highlight={i === highlightIdx}
          onTap={() => onTap?.(m)}
        />
      ))}
    </div>
  );
}

function MemberTile({ m, c, highlight, onTap }) {
  const color = colorOf(m.accent_color || m.color);
  const isPet = m.role === "pet";
  const logged = !!(
    c && (c.mood != null || c.sleep != null || (isPet && c.walks?.length))
  );
  const mood = c?.mood;
  const moodSym = mood != null ? moodEmoji(mood) : null;
  // Background: humans get the mood tint (matches the dashboard BigTile
  // — a logged 9/10 reads green, a logged 2/10 reads rough). Pets stay
  // on their avatar colour since they don't have moods; unlogged humans
  // stay on paper.
  const bg = (() => {
    if (!logged) return "var(--bg-soft)";
    if (isPet) {
      const pv = petWalkVibe(c);
      return pv?.tint && pv.level !== "unknown" ? pv.tint : color.hex;
    }
    const vibe = computeVibe(c);
    return vibe?.tint || color.hex;
  })();
  return (
    <button
      type="button"
      onClick={onTap}
      className={`rounded-[20px] border border-[color:var(--line)] p-5 md:p-6 flex flex-col items-center text-center transition-all duration-700 ease-out active:scale-[0.98] ${
        logged ? "opacity-100" : "opacity-75"
      }`}
      style={{
        background: bg,
        transform: highlight ? "scale(1.04)" : "scale(1)",
        boxShadow: highlight
          ? "0 18px 50px -20px rgba(0,0,0,0.25)"
          : "0 0 0 rgba(0,0,0,0)",
      }}
      data-testid={`display-tile-${m.id}`}
      aria-label={`Check in ${m.name}`}
    >
      <div
        className="w-20 h-20 md:w-28 md:h-28 rounded-full flex items-center justify-center text-[48px] md:text-[72px] overflow-hidden mb-3"
        style={{ background: logged ? "transparent" : color.hex }}
      >
        {m.avatar_image ? (
          <img
            src={m.avatar_image}
            alt={m.name}
            className="w-full h-full object-cover"
          />
        ) : (
          m.avatar_emoji
        )}
      </div>
      <div className="font-display text-xl md:text-2xl leading-tight tracking-tight">
        {m.name}
      </div>
      <div className="label-tiny text-[color:var(--ink-2)] mt-1 min-h-[16px]">
        {isPet
          ? (() => {
              const species = (m.pet_species || "").toLowerCase();
              const walksRelevant = species === "dog" || species === "cat" || !species;
              const walks = c?.walks?.length || 0;
              if (walksRelevant) {
                return walks
                  ? `${walks} walk${walks > 1 ? "s" : ""} today`
                  : "needs walking";
              }
              const feeds = (c?.feeds?.length ?? 0) + (c?.fed_at ? 1 : 0);
              const water = (c?.water_changes?.length ?? 0) + (c?.water_change_at ? 1 : 0);
              if (feeds > 0) return `${feeds} feed${feeds > 1 ? "s" : ""} today`;
              if (water > 0) return "fresh water today";
              return "not yet";
            })()
          : logged
            ? (moodSym ? `${moodSym} mood ${mood}` : "checked in")
            : "not yet"}
      </div>
    </button>
  );
}


// ---- Calendar-only mode helpers ----------------------------------------

// Compact event tile — used inside CalendarGroups for tomorrow + later
// rows. Mirrors the visual shape of the previous flat-grid tile so
// regulars don't notice anything has changed for events that aren't
// today; today gets the BIG horizontal HappeningNowCard above instead.
function EventTile({ ev, members, navigate, fadeCal, colorCodeByMember = false }) {
  const { lang } = useI18n();
  const k = kindOf(ev.kind);
  const isBirthday = ev.source === "birthday";
  const attendees = (ev.member_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter((m) => m && (isBirthday ? true : m.role !== "pet"));
  // Color-code mode: the card always goes neutral when the family
  // has opted in — the right-edge rail (when there's anyone tagged)
  // is the colour signal. Events without attendees (manually-logged
  // friend birthdays, ICS imports etc.) keep the neutral surface so
  // the calendar reads as one coherent system.
  const neutral = colorCodeByMember;
  const showStripe = neutral && attendees.length > 0;
  return (
    <button
      type="button"
      onClick={() => navigate(`/event/${encodeURIComponent(ev.id)}`, { state: { event: ev, from: "/display" } })}
      className="ff-card w-full text-left p-3 md:p-4 rounded-[12px] flex items-center gap-3 md:gap-4 relative overflow-hidden"
      data-testid={`display-event-${ev.id}`}
      style={{
        background: neutral ? "var(--surface)" : softenTint(k.tint, fadeCal),
        border: neutral ? "1px solid rgba(11,11,9,0.10)" : undefined,
        // Reserve right padding so content doesn't sit under the rail.
        // 24px stripe + 6px breathing.
        paddingRight: showStripe ? "30px" : undefined,
      }}
    >
      {showStripe && (
        <div
          className="absolute top-0 right-0 bottom-0 flex flex-col rounded-r-[12px] overflow-hidden pointer-events-none"
          style={{ width: "24px" }}
          aria-hidden="true"
          data-testid={`display-event-accents-${ev.id}`}
        >
          {attendees.slice(0, 6).map((m) => (
            <span
              key={m.id}
              className="flex-1 block"
              style={{ background: memberAccentHex(m) }}
              title={m.name}
            />
          ))}
        </div>
      )}
      {isBirthday && (
        <>
          <span className="pointer-events-none absolute top-1 right-3 text-base md:text-xl opacity-80 rotate-12" aria-hidden="true">🎊</span>
          <span className="pointer-events-none absolute bottom-1 right-7 text-sm md:text-lg opacity-70 rotate-6" aria-hidden="true">🎉</span>
        </>
      )}
      <span
        className="flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-xl md:text-2xl leading-none overflow-hidden"
        aria-hidden="true"
      >
        {/* Birthday cards now lead with 🎉 on the left — keeps the
            same left-emoji, right-attendee-avatar rhythm as every
            other card, so the eye scans a vertical list consistently. */}
        {isBirthday ? "🎉" : eventEmoji(ev)}
      </span>
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        <div className="font-display text-base md:text-lg leading-tight tracking-tight line-clamp-2 text-[color:var(--accent)]">
          {ev.title}
          {ev.attachment_image && (
            <span
              className="ml-1 text-[12px] md:text-[13px] opacity-70 align-middle"
              title="Photo attached"
              data-testid={`display-event-attachment-${ev.id}`}
            >
              📎
            </span>
          )}
        </div>
        <span className="label-tiny text-[color:var(--ink)] truncate text-[10.5px] md:text-[11px]">
          {fmtEventWhen(ev.starts_at, ev.ends_at, lang)}
        </span>
      </div>
      {attendees.length > 0 && (
        <div className="flex -space-x-1.5 flex-shrink-0">
          {attendees.slice(0, 3).map((m) => (
            <span
              key={m.id}
              className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[color:var(--bg)] border-2 border-[color:var(--surface)] flex items-center justify-center text-base overflow-hidden"
              title={m.name}
            >
              {m.avatar_image ? (
                <img src={m.avatar_image} alt="" className="w-full h-full object-cover" />
              ) : (
                m.avatar_emoji
              )}
            </span>
          ))}
          {attendees.length > 3 && (
            <span className="label-tiny ml-1.5 self-center text-[11px] text-[color:var(--ink-3)]">+{attendees.length - 3}</span>
          )}
        </div>
      )}
    </button>
  );
}

// Big horizontal "today" card — one row per event happening today,
// large title + attendee row, designed to dominate the top of the
// display the way a chalkboard "TODAY" line would.
//
// `state` says where in the urgency spectrum we are:
//   • "now"  — event is currently active. Badge reads "● Happening now".
//   • "soon" — starts within the next 60 minutes. Badge reads "● soon".
// Both states get the FULL kind tint (no tint-fade), so on teen mode
// they pop against the muted later-today / future tiles.
function HappeningNowCard({ ev, members, navigate, state = "soon", colorCodeByMember = false, onOpenLightbox }) {
  const { lang } = useI18n();
  const k = kindOf(ev.kind);
  // `source: "birthday"` are synthesized FAMILY birthdays — full-day
  // celebration card. Real birthday events with the same kind but
  // without that source are "friend" birthdays (logged manually).
  const isFamilyBirthday = ev.source === "birthday";
  const isFriendBirthday =
    ev.kind === "birthday" && !isFamilyBirthday;
  const attendees = (ev.member_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter((m) => m && (isFamilyBirthday ? true : m.role !== "pet"));
  const bdPerson = isFamilyBirthday ? attendees[0] : null;
  // Pull the age out of a synthesized birthday title ("Charlie's
  // birthday · turns 7"). The synth keeps the age inline; we surface
  // it as a large numeral on the card.
  const familyBdayAge = (() => {
    if (!isFamilyBirthday) return null;
    const m = (ev.title || "").match(/turns (\d+)/i);
    return m ? Number(m[1]) : null;
  })();

  // "Whole-family trip of 3+ days" detector — used to swap in a warm
  // "Have a good trip ✈️" subline on the hero card. A shared trip is
  // a nice moment to make the display a little more personal than a
  // timestamp. Rules:
  //   • kind is travel OR day_trip (prevents a 3-day dinner glitch
  //     from reading like a holiday)
  //   • ends_at set, span ≥ 3 calendar days (inclusive — Fri→Sun
  //     counts as 3 days)
  //   • every non-pet family human is tagged; if a family has only 2
  //     adults, "both" is the whole family too
  const isWholeFamilyTrip = (() => {
    if (ev.kind !== "travel" && ev.kind !== "day_trip") return false;
    if (!ev.ends_at) return false;
    const s = new Date(ev.starts_at);
    const e = new Date(ev.ends_at);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
    const msPerDay = 86400000;
    const days =
      Math.floor(
        (new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime()
          - new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime())
          / msPerDay,
      ) + 1;
    if (days < 3) return false;
    const humans = (members || []).filter((m) => m.role !== "pet");
    if (humans.length < 2) return false;
    return humans.length > 0 && attendees.length >= humans.length;
  })();

  const bg = softenTint(k.tint, false); // hero cards always full tint
  // Color-code mode: hero card also goes neutral (matches EventTile
  // / CalendarSheet) so a non-family birthday or an attendee-less
  // event doesn't keep its kind colour while everything else loses
  // it. The 32px right rail only renders when there's someone to
  // colour-code; otherwise the card is simply neutral.
  const neutral = colorCodeByMember;
  const showStripe = neutral && attendees.length > 0;
  return (
    <button
      type="button"
      onClick={() => navigate(`/event/${encodeURIComponent(ev.id)}`, { state: { event: ev, from: "/display" } })}
      className="ff-card w-full text-left p-4 md:p-6 rounded-[14px] relative overflow-hidden flex items-center gap-4 md:gap-6"
      data-testid={`display-today-${ev.id}`}
      style={{
        background: neutral ? "var(--surface)" : bg,
        border: neutral ? "1px solid rgba(11,11,9,0.10)" : undefined,
        // Hero card gets a chunkier 32px rail so the colour reads from
        // across the kitchen at the same density as the smaller tiles.
        paddingRight: showStripe ? "40px" : undefined,
      }}
    >
      {showStripe && (
        <div
          className="absolute top-0 right-0 bottom-0 flex flex-col rounded-r-[14px] overflow-hidden pointer-events-none"
          style={{ width: "32px" }}
          aria-hidden="true"
          data-testid={`display-today-accents-${ev.id}`}
        >
          {attendees.slice(0, 6).map((m) => (
            <span
              key={m.id}
              className="flex-1 block"
              style={{ background: memberAccentHex(m) }}
              title={m.name}
            />
          ))}
        </div>
      )}
      <span
        className="absolute bottom-2 right-3 label-tiny text-[10px] md:text-[11px] font-display-italic text-[color:var(--ink-2)] flex items-center gap-1"
        aria-hidden="true"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent,#d97706)]" />
        {state === "now" ? "Happening now" : "soon"}
      </span>
      {(isFamilyBirthday || isFriendBirthday) && (
        <>
          <span className="pointer-events-none absolute top-2 right-4 text-2xl opacity-80 rotate-12" aria-hidden="true">🎊</span>
          <span className="pointer-events-none absolute bottom-2 left-4 text-xl opacity-70 -rotate-12" aria-hidden="true">🎈</span>
        </>
      )}
      <span
        className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-4xl md:text-5xl leading-none overflow-hidden"
        aria-hidden="true"
      >
        {isFamilyBirthday && bdPerson ? (
          bdPerson.avatar_image ? (
            <img src={bdPerson.avatar_image} alt="" className="w-full h-full object-cover" />
          ) : (
            bdPerson.avatar_emoji
          )
        ) : (
          eventEmoji(ev)
        )}
      </span>
      <div className="min-w-0 flex-1">
        {isFamilyBirthday ? (
          <>
            {/* Inline name + age on a tighter single layout so a short
                celebrant name ("HC") doesn't collide with the
                floating 🎊 emoji pinned to the top-right of the
                hero card. `pr-10` reserves the right corner for the
                floating emoji; age moves inline at a small tabular
                size instead of its previous huge solo row that was
                forcing the title to wrap behind the emoji. */}
            <div className="font-display text-2xl md:text-4xl leading-[1.1] tracking-tight text-[color:var(--accent)] pr-10 md:pr-12 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span>Happy birthday, {bdPerson?.name || "you"}!</span>
              {familyBdayAge !== null && (
                <span className="font-display tabular text-[color:var(--ink)] text-xl md:text-2xl">
                  {familyBdayAge}
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Attached photo rendered inline above the title — turns
                the kitchen iPad into an ambient bulletin board. The
                actual event ticket / flyer / receipt is visible at a
                glance without anyone having to tap. Slightly larger on
                iPad (md) so it reads across the room. Hidden for
                birthdays (they already have a big avatar). */}
            {ev.attachment_image && (
              <div className="mb-2.5 md:mb-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onOpenLightbox) onOpenLightbox(ev.attachment_image);
                  }}
                  className="block p-0 border-0 bg-transparent cursor-pointer"
                  aria-label="Open attached photo"
                  data-testid={`display-today-attachment-open-${ev.id}`}
                >
                  <img
                    src={ev.attachment_image}
                    alt=""
                    className="block w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover border border-[color:var(--surface)] shadow-sm"
                    data-testid={`display-today-attachment-thumb-${ev.id}`}
                  />
                </button>
              </div>
            )}
            <div className="font-display text-2xl md:text-4xl leading-[1.05] tracking-tight">
              {ev.title}
            </div>
            <div className="label-tiny text-[color:var(--ink)] mt-1.5">
              {fmtEventWhen(ev.starts_at, ev.ends_at, lang)}
            </div>
            {isWholeFamilyTrip && (
              <div className="font-display-italic text-base md:text-lg text-[color:var(--ink-2)] mt-1.5" data-testid="display-wholefamily-trip-line">
                {lang === "da" ? "God tur ✈️" : "Have a good trip ✈️"}
              </div>
            )}
            {ev._bdayPrompt && (
              <div className="font-display-italic text-sm md:text-base text-[color:var(--ink-2)] mt-1.5">
                Did you wish them happy birthday?
              </div>
            )}
            {attendees.length > 0 && (
              <div className="flex items-center -space-x-2 mt-3">
                {attendees.slice(0, 8).map((m) => (
                  <span
                    key={m.id}
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[color:var(--bg)] border-2 border-[color:var(--surface)] flex items-center justify-center text-base overflow-hidden"
                    title={m.name}
                  >
                    {m.avatar_image ? (
                      <img src={m.avatar_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      m.avatar_emoji
                    )}
                  </span>
                ))}
                {attendees.length > 8 && (
                  <span className="label-tiny ml-3 self-center">+{attendees.length - 8}</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </button>
  );
}


// Renders the calendar-only event grid in time-bucketed groups with
// small italic section labels between them. Today's events are
// promoted to a big horizontal HappeningNowCard above the grid.
function CalendarGroups({ events, members, navigate, fadeCal, colorCodeByMember = false, onOpenLightbox }) {
  const { lang } = useI18n();
  // Bucket every non-today event by its day-key, preserving order.
  const today = todayKey();
  const todayEvents = events.filter((e) => eventIsOnDay(e, today));
  const restByDay = new Map();
  for (const ev of events) {
    const k = dayKeyOf(ev.starts_at);
    if (!k || k === today) continue;
    // Multi-day events that already started but haven't ended yet
    // are shown in today's hero (via `todayEvents` above), so don't
    // also duplicate them under a past "started Friday" group.
    if (eventIsOnDay(ev, today)) continue;
    if (!restByDay.has(k)) restByDay.set(k, []);
    restByDay.get(k).push(ev);
  }
  const dayKeys = Array.from(restByDay.keys()).sort();

  // Compact "Tomorrow", "In 2 days", "Friday May 9" style label.
  const groupLabel = (key) => {
    const base = dayLabel(key, lang); // "Today"/"I dag" | "Tomorrow"/"I morgen" | "Monday Apr 28"
    if (base === "Today" || base === "Tomorrow" || base === "I dag" || base === "I morgen") return base;
    // Compute "in N days" where reasonable (2-7 day range), fall back
    // to the full weekday/date label for anything further out.
    const [y, m, d] = key.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const now = new Date();
    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const days = Math.round((dt - today0) / 86400000);
    if (days >= 2 && days <= 7) return lang === "da" ? `Om ${days} dage` : `In ${days} days`;
    return base;
  };

  return (
    <div className="h-full overflow-y-auto no-scrollbar pr-2 flex flex-col gap-5 md:gap-6 content-start">
      {(() => {
        // Split today's events into three urgency buckets:
        //   • now      — currently happening (start ≤ now ≤ effectiveEnd).
        //                Cards stay sticky for the event's whole window
        //                (real ends_at, or kind-default duration), so a
        //                fishing trip 13:00-18:00 keeps the family in the
        //                loop the entire afternoon.
        //   • soon     — starts within the next 60 minutes.
        //   • later    — rest of today.
        //
        // FRIEND birthdays (kind=birthday set as all-day, NOT a synthesized
        // family birthday) are special: they flash big at 7-8am ("today's
        // X's birthday!") and again at 8-9pm with a "did you wish them?"
        // nudge — outside those windows they live in the calendar groups
        // below so the hero stays meaningful.
        const nowMs = Date.now();
        const soonCutoffMs = nowMs + 60 * 60 * 1000;
        const nowDate = new Date(nowMs);
        const hourOfDay = nowDate.getHours();
        const buckets = { now: [], soon: [], later: [] };
        // Away strip — collect everyone whose multi-day travel /
        // day_trip is active today. Renders as a small avatar row at
        // the top of the events list ("Momme is away in Tyrkiet"),
        // not a big "Happening now" hero. The multi-day trip itself
        // gets pulled out of the today buckets so it doesn't double
        // up. Single-day day_trips still appear as a hero — they're
        // "today's lunch out", not a multi-week absence.
        const awayItems = [];
        const isMultiDayAway = (ev) => {
          if (ev.kind !== "travel" && ev.kind !== "day_trip") return false;
          if (!ev.ends_at) return false;
          const s = new Date(ev.starts_at);
          const e = new Date(ev.ends_at);
          if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
          const sDay = new Date(s.getFullYear(), s.getMonth(), s.getDate()).getTime();
          const eDay = new Date(e.getFullYear(), e.getMonth(), e.getDate()).getTime();
          return eDay > sDay; // ends on a different calendar day than it starts
        };
        for (const ev of todayEvents) {
          const startMs = new Date(ev.starts_at).getTime();
          if (Number.isNaN(startMs)) {
            buckets.later.push(ev);
            continue;
          }
          // Multi-day away — route to the away strip instead of a
          // big hero card. Only when the event is currently active
          // (start ≤ now ≤ end).
          if (isMultiDayAway(ev)) {
            const endMs = effectiveEndMs(ev);
            if (startMs <= nowMs && (endMs === null || endMs >= nowMs)) {
              awayItems.push(ev);
              continue;
            }
          }
          // Friend birthday windowing — only family-member birthdays
          // (source==="birthday") are full-day "happening now".
          const isFriendBday =
            ev.kind === "birthday" &&
            ev.source !== "birthday" &&
            isAllDayLocal(ev.starts_at);
          if (isFriendBday) {
            const inMorning = hourOfDay >= 7 && hourOfDay < 8;
            const inEvening = hourOfDay >= 20 && hourOfDay < 21;
            if (inMorning || inEvening) {
              buckets.now.push({ ...ev, _bdayPrompt: inEvening });
            } else {
              buckets.later.push(ev);
            }
            continue;
          }
          const endMs = effectiveEndMs(ev);
          // Skip events whose effective end is already in the past —
          // they're done for the day.
          if (endMs !== null && endMs < nowMs) {
            continue;
          }
          if (startMs <= nowMs && (endMs === null || endMs >= nowMs)) {
            buckets.now.push(ev);
          } else if (startMs > nowMs && startMs <= soonCutoffMs) {
            buckets.soon.push(ev);
          } else {
            buckets.later.push(ev);
          }
        }

        const heroSections = [];
        if (buckets.now.length > 0) {
          heroSections.push({
            key: "now",
            // No section header — the card itself carries the
            // "● Happening now" badge in its lower-right corner.
            label: null,
            events: buckets.now,
            state: "now",
          });
        }
        if (buckets.soon.length > 0) {
          heroSections.push({
            key: "soon",
            // No section header for "soon" — the card itself already
            // carries a small "● soon" badge in its lower-right
            // corner, so a separate "COMING UP SOON" eyebrow above it
            // just doubled up the message and felt shouty.
            label: null,
            events: buckets.soon,
            state: "soon",
          });
        }

        return (
          <>
            {/* Away strip — small avatar pills above the main "today"
                cards. One pill per multi-day trip currently active.
                Avatar on the left, "[Name] is away in [Place]" on the
                right. Tap to jump to the event page. Pets are not
                included (they don't "go away" to Tyrkiet alone). */}
            {awayItems.length > 0 && (
              <section
                className="flex flex-wrap gap-2"
                data-testid="display-away-strip"
              >
                {awayItems.map((ev) => {
                  const people = (ev.member_ids || [])
                    .map((id) => (members || []).find((m) => m.id === id))
                    .filter((m) => m && m.role !== "pet");
                  if (people.length === 0) return null;
                  // Strip the attendee's own name from the title so
                  // "Momme Tyrkiet" tagged with Momme reads as
                  // "Tyrkiet". Falls back to "away" when nothing
                  // useful remains.
                  let dest = (ev.title || "").trim();
                  for (const m of people) {
                    const re = new RegExp(
                      `^${m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b'?s?\\s*`,
                      "i",
                    );
                    dest = dest.replace(re, "").trim();
                  }
                  dest = dest.replace(/\s+(trip|holiday|vacation)$/i, "").trim();
                  const names = people.slice(0, 2).map((m) => m.name);
                  const who =
                    names.length === 1
                      ? names[0]
                      : `${names[0]} & ${names[1]}`;
                  const verb = people.length > 1 ? "are" : "is";
                  const text = dest
                    ? `${who} ${verb} in ${dest}`
                    : `${who} ${verb} away`;
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() =>
                        navigate(`/event/${encodeURIComponent(ev.id)}`, {
                          state: { event: ev, from: "/display" },
                        })
                      }
                      className="ff-card ff-card-hover inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                      data-testid={`display-away-${ev.id}`}
                    >
                      <span className="w-7 h-7 rounded-full overflow-hidden bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-base leading-none flex-shrink-0">
                        {people[0].avatar_image ? (
                          <img
                            src={people[0].avatar_image}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          people[0].avatar_emoji
                        )}
                      </span>
                      <span className="text-[13px] leading-tight">
                        {text}
                      </span>
                    </button>
                  );
                })}
              </section>
            )}
            {heroSections.map((s) => (
              <section
                key={s.key}
                className="flex flex-col gap-2.5 md:gap-3"
                data-testid={`display-hero-${s.key}`}
              >
                {s.label && (
                  <h2 className="label-tiny font-display-italic text-[color:var(--ink-3)] mb-0.5">
                    {s.label}
                  </h2>
                )}
                {s.events.map((ev) => (
                  <HappeningNowCard
                    key={ev.id}
                    ev={ev}
                    members={members}
                    navigate={navigate}
                    state={s.state}
                    colorCodeByMember={colorCodeByMember}
                    onOpenLightbox={onOpenLightbox}
                  />
                ))}
              </section>
            ))}
            {/* "Later today" — compact tiles for the rest of today */}
            {buckets.later.length > 0 && (
              <section className="flex flex-col gap-2.5 md:gap-3">
                <h2 className="label-tiny font-display-italic text-[color:var(--ink-3)] mb-0.5">
                  Later today
                </h2>
                <div
                  className={
                    // Single-event sections stay full-width (no empty
                    // half on a 1-of-2 grid). Multi-event sections go
                    // 2-up on iPad portrait (md) and 3-up on landscape
                    // / desktop (xl) so the kitchen-mounted tablet
                    // actually uses its real estate.
                    (buckets.later.length === 1
                      ? "flex flex-col gap-2 md:gap-2.5"
                      : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-3") + " ff-stagger"
                  }
                >
                  {buckets.later.map((ev) => (
                    <EventTile
                      key={ev.id}
                      ev={ev}
                      members={members}
                      navigate={navigate}
                      fadeCal={fadeCal}
                      colorCodeByMember={colorCodeByMember}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        );
      })()}
      {/* Other days — labelled compact rows. Same rule as above:
         single-event days stay full-width, multi-event days fan out
         2-up on iPad and 3-up on desktop. */}
      {dayKeys.map((key) => {
        const dayEvents = restByDay.get(key);
        return (
          <section key={key} className="flex flex-col gap-2.5 md:gap-3">
            <h2 className="label-tiny font-display-italic text-[color:var(--ink-3)] mb-0.5">
              {groupLabel(key)}
            </h2>
            <div
              className={
                dayEvents.length === 1
                  ? "flex flex-col gap-2 md:gap-2.5"
                  : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-3"
              }
            >
              {dayEvents.map((ev) => (
                <EventTile
                  key={ev.id}
                  ev={ev}
                  members={members}
                  navigate={navigate}
                  fadeCal={fadeCal}
                  colorCodeByMember={colorCodeByMember}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

